<template>
  <v-app class="premium-app">
    <v-progress-linear 
      v-if="loading"
      indeterminate
      class="premium-global-loader"
      color="primary"
      height="3"
    />

    <v-app-bar
      :style="[
        loading ? 'margin-top: 3px;' : '',
        'transition: all 0.5s ease-in-out',
        'background-color: var(--app-color-background);'
      ]"
      v-if="smAndDown"
    >
      <template #prepend>
        <img
          src="@/assets/images/logo.png" 
          alt="Conduril logo in white" 
          srcset=""  
          height="40px"      
          class="px-4"  
        />
      </template>

      <template v-slot:append>
        <v-app-bar-nav-icon
          @click="openMenu"
          :icon="!showMenu ? 'mdi-menu' : 'mdi-close'"
        ></v-app-bar-nav-icon>
      </template>
    </v-app-bar>

    <v-navigation-drawer
      class="premium-drawer"
      v-model="showMenu"
      :rail="(mdAndDown || showMini) && !smAndDown"
      :width="250"
      :rail-width="64"
      location="left"
      elevation="6"
      floating
      :permanent="mdAndUp"
      :expand-on-hover="smAndUp"
    >
      <div class="drawer-content d-flex flex-column">
        <img 
          v-if="!smAndDown"
          src="@/assets/images/logo.png" 
          alt="Conduril logo" 
          srcset=""
          class="sidebar-logo"
        >

        <div 
          class="premium-navigation flex-grow-1 d-flex flex-column"
          
        >
          <template v-if="loading">
            <div class="nav-skeleton flex-grow-1">
              <v-skeleton-loader
                v-for="i in 6"
                :key="i"
                color="dark"
                type="list-item"
                class="skeleton-item"
              />
            </div>
          </template>

          <v-list 
            v-else
            v-model:opened="openMenus"
            prepend-gap="1rem"
            indent="2rem"
            nav 
            class="premium-nav-list flex-grow-1" 
            style="max-height: 70vh; overflow-y: auto"
          >
            <template v-for="item in items" :key="item.title">
              <v-list-item 
                class="premium-nav-item" 
                link 
                :to="item.link" 
                :exact="item.exact"
                :prepend-icon="item.icon"
                :title="showMenu ? item.title : ''"
                v-if="item.items == 0 && item.has_permission"
                style="border-radius: 10px;"
              >
              </v-list-item>  

              <v-list-group
                :value="item.group"
                class="premium-nav-group" 
                style="border-radius: 10px;"
                v-else-if="item?.items?.length > 0 && item.has_permission"
              >
                <template v-slot:activator="{ props }">
                  <v-list-item 
                    class="premium-nav-item group-header" 
                    v-bind="props"
                    :prepend-icon="item.icon"
                    :title="showMenu ? item.title : ''"
                    @click="() => {
                      showMini = false;
                      openMenus.push(item.title);
                    }"
                    style="border-radius: 10px;"
                  />
                </template>
                
                <template v-for="child in item.items" :key="child.title">
                  <v-list-item 
                    class="premium-nav-item sub-item" 
                    link 
                    :to="child.link" 
                    :exact="child.exact"
                    :prepend-icon="child.icon"
                    :title="showMenu ? child.title : ''"
                    v-if="child.has_permission"
                    style="border-radius: 10px;"
                  >
                  </v-list-item>
                </template>
              </v-list-group>
            </template>
          </v-list>

          <v-list>
            <v-list-item 
              class="premium-nav-item"
              link 
              to="" 
              :exact="true"
              prepend-icon="mdi-power"
              :title="showMenu ? 'Terminar Sessão' : ''"
              @click.prevent="logout"
              style="padding-block: 2rem;"
            >
            </v-list-item>
          </v-list>
        </div>
      </div>
    </v-navigation-drawer>

    <v-main class="premium-main">
      <v-alert
        v-if="sessionError"
        type="error"
        variant="tonal"
        :title="sessionErrorTitle"
        :text="sessionError"
        closable
      >
      </v-alert>
      
      <div
        class="premium-content-container"
        v-if="!loading && !sessionError"
      >
        <router-view />
      </div>
    </v-main>

    <v-footer class="premium-footer flex-0-0">
      <div class="footer-content">
      </div>
    </v-footer>

    <UniversalDialog>
      <template #headerTitle>
        <span></span>
      </template>

      <template #footer="{ options, isLoading, confirm, cancel }">
        <v-alert
          v-if="options.onConfirmFailed"
          type="error"
          :title="options.onConfirmFailedErrorTitle"
          :text="options.onConfirmFailedErrorMessage"
          variant="outlined"
          class="ma-4"
          style="min-height: 70px;"
        />
        <v-divider v-if="options.showActions" />
        <v-card-actions
          v-if="options.showActions"
          class="pa-4 justify-center ga-4"
        >
          <v-btn
            v-if="options.showCancel"
            :color="options.cancelActionColor"
            :variant="options.cancelActionVariant"
            :class="[
              options.cancelActionClasses,
              'w-25'
            ]"
            :prepend-icon="
              isLoading
              ? options?.onCancelActionIcon ?? options.cancelActionIcon
              : options.cancelActionIcon
            "
            :disabled="isLoading"
            @click="cancel"
          >
            {{ 
              isLoading
              ? options?.onCancelActionText ?? options.cancelActionText
              : options.cancelActionText
            }}
          </v-btn>
          <v-btn
            :color="options.confirmActionColor"
            :variant="options.confirmActionVariant"
            :prepend-icon="
              isLoading
                ? options.onConfirmActionIcon
                : options.confirmActionIcon
            "
            :class="[
              options.confirmActionClasses,
              options.showCancel ? 'w-50' : 'w-75',
            ]"
            @click="confirm"
            :disabled="isLoading"
          >
            {{
              isLoading
                ? options.onConfirmActionText
                : options.confirmActionText
            }}
          </v-btn>
        </v-card-actions>
      </template>
    </UniversalDialog>
  </v-app>
</template>

<script setup>
import {
  ref,
  reactive,
  onMounted,
  watch,
  getCurrentInstance,
  computed,
  onBeforeMount,
} from 'vue'

import {
  useRouter,
  useRoute,
} from 'vue-router'

import { useTheme } from 'vuetify'
import { useDisplay } from 'vuetify'

import { useSessionStore } from '@/stores/SessionStore'

import UniversalDialog from '@internal_projects_dd/universal-dialog'

onBeforeMount(() => {
  document.title = 'Nexus'
})

const router = useRouter()
const route = useRoute()
const theme = useTheme()
const instance = getCurrentInstance()

const sessionStore = useSessionStore();
// NEXUS-148 - Alice Serina - 21/10/2026
// getting session error information from the global (singleton) session
const sessionError = computed(
    () => sessionStore.sessionError?.message ?? null,
);

const sessionErrorTitle = computed(
    () => sessionStore.sessionError?.title ?? null,
);
const themes = instance.appContext.config.globalProperties.$themes

const showMenu = ref(true)
const showMini = ref(true)
const items = ref([])
const loading = ref(true)
const rotateAvatar = ref(false)
const hover = ref(false)
const canRotate = ref(true)

const openMenus = ref([])

const {
  width,
  lgAndUp,
  lgAndDown,
  mdAndDown,
  mdAndUp,
  smAndDown,
  smAndUp,
} = useDisplay()

const routesForBreadCrumbs = computed(() => {
  const crumbs = []

  const isLayoutRoute = (routeRecord) => {
    return routeRecord?.components?.default?.__name?.includes('Layout')
      || routeRecord?.components?.default?.__file?.includes('Layout.vue')
  }

  route.matched.forEach((matchedRoute) => {
    if (!isLayoutRoute(matchedRoute)) {
      crumbs.push(matchedRoute)
    } else if (!matchedRoute?.name) {
      const basePathFromChildren = matchedRoute?.children?.filter(
        (childRoute) => {
          if (
            childRoute?.path === ''
            && childRoute?.name
          ) {
            return {
              ...childRoute,
              path: matchedRoute.path,
            }
          }

          return false
        }
      )

      crumbs.splice(-1, 0, ...basePathFromChildren)
    }
  })

  return crumbs
})

watch(showMenu, (value) => {
  localStorage['menu-show'] = value
}, {
  immediate: false,
})

watch(showMini, (value) => {
  localStorage['menu-mini'] = value

  if (showMini.value) {
    openMenus.value = []
  }
}, {
  immediate: false,
})

watch(
  () => sessionStore.user?.theme, 
  () => {
    if (!sessionStore.user?.theme) {
      return
    }

    let currentTheme = sessionStore.user?.theme

    currentTheme = currentTheme.length === 0
      ? themes[0]
      : currentTheme

    setTheme(currentTheme)
    setCssVars()
  }, {
    immediate: true,
    deep: true,
})

const startRotation = () => {
  if (canRotate.value) {
    hover.value = true
    rotateAvatar.value = true
    canRotate.value = false

    setTimeout(() => {
      canRotate.value = true
    }, 60000)
  }
}

const stopRotation = () => {
  hover.value = false
  rotateAvatar.value = false
}

const getVersion = () => {
  return process.env.VUE_APP_SECRET_VERSION
}

const setCssVars = () => {
}

const setTheme = (themeConfig) => {
  if (!themeConfig || !theme?.themes?.value) {
    return
  }
  
  const dark = themeConfig.dark
  const light = themeConfig.light
  
  Object.keys(dark).forEach((key) => { 
    if (theme.themes.value.dark.colors) {
      theme.themes.value.dark.colors[key] = dark[key] 
    }
  })

  Object.keys(light).forEach((key) => { 
    if (theme.themes.value.light.colors) {
      theme.themes.value.light.colors[key] = light[key] 
    }
  })
}

const toggleTheme = () => {
  const newTheme = theme.global.name.value === 'dark'
    ? 'light'
    : 'dark'

  theme.global.name.value = newTheme
  localStorage['dark-mode'] = newTheme === 'dark' ? 1 : 0
}

/*
 * O botão "Terminar Sessão" faz somente redirect
 * para o portal configurado no ambiente.
 *
 * Não termina sessão no Keycloak.
 * Não chama API.
 * Não limpa storage.
 * Não usa fallback.
 */
const logout = () => {
  window.location.href = process.env.VUE_APP_LOGIN_PORTAL_URL + 'dashboard'
}

const openMenu = () => {
  const isMobile = window.innerWidth < 1500
  
  if (!isMobile) {
    showMini.value = !showMini.value
    return
  }

  showMini.value = false
  showMenu.value = !showMenu.value
}

const setMenu = () => {
  if (
    !sessionStore.isHydrated
    || !sessionStore.user
  ) {
    return
  }

  const menu = [
    {
      title: 'Dashboard',
      icon: 'mdi-view-dashboard',
      has_permission: true,
      exact: true,
      link: '/admin',
      items: [],
    },
    {
      title: 'Mudança de base de trabalho',
      icon: 'mdi-home-switch-outline',
      has_permission: sessionStore.hasPermission([
        "user.department.membership.request.list.any",
        "user.department.membership.request.list.department",
        "user.department.membership.request.view.operators.any",
        // "user.department.membership.request.list.self"
      ]),
      exact: true,
      link: '/departments/membership-requests',
      items: [
        // {
        //   title: "Criar MBT",
        //   icon: "mdi-plus",
        //   has_permission: sessionStore.hasPermission([
        //     "user.department.membership.request.create.any",
        //     "user.department.membership.request.create.department",
        //     "user.department.membership.request.create.self",
        //   ]),
        //   link: "/departments/membership-requests/create",
        //   exact: true,
        //   items: [],
        // },
        {
          title: "Listar MBTs",
          icon: "mdi-view-list",
          has_permission: sessionStore.hasPermission([
            "user.department.membership.request.list.any",
            "user.department.membership.request.list.department",
            "user.department.membership.request.list.self",
            "user.department.membership.request.view.operators.any"
            
          ]),
          link: "/departments/membership-requests",
          exact: true,
          items: [],
        },
        {
          title: "Criar Grupo de Acompanhamento",
          icon: "mdi-map-marker-plus",
          has_permission: sessionStore.hasPermission([
            "user.department.membership.request.tracking-group.create",
            "user.department.membership.request.tracking-group.manage.any",
          ]),
          link: "/departments/membership-requests/tracking-groups/create",
          exact: true,
          items: [],
        },
        {
          title: "Listar Grupos de Acompanhamento",
          icon: "mdi-map-marker-account",
          has_permission: sessionStore.hasPermission([
            "user.department.membership.request.tracking-group.update",
            "user.department.membership.request.tracking-group.delete",
            "user.department.membership.request.tracking-group.get",
            "user.department.membership.request.tracking-group.list",
            "user.department.membership.request.tracking-group.manage.manager",
            "user.department.membership.request.tracking-group.manage.tracked",
            "user.department.membership.request.tracking-group.manage.any",

          ]),
          link: "/departments/membership-requests/tracking-groups",
          exact: true,
          items: [],
        }
       ],
    },
    {
      title: "Férias",
      icon: "mdi-chat-sleep",
      has_permission: sessionStore.hasPermission([
        "absence.request.create.any",
        "absence.request.create.department",
        "absence.request.create.self",
      ]),
      exact: true,
      link: "",
      items: [
        {
          title: "Pedidos",
          icon: "mdi-list-box-outline",
          has_permission: sessionStore.hasPermission([
            "absence.request.create.any",
            "absence.request.create.department",
            "absence.request.create.self",
          ]),
          exact: true,
          link: "/absences/requests",
          items: [],
        },
        {
          title: "Mapa",
          icon: "mdi-chart-gantt",
          has_permission: sessionStore.hasPermission([
            "absence.request.list.any",
            "absence.request.list.department",
          ]),
          exact: true,
          link: "/absences/requests/map",
          items: [],
        },
        {
          title: "Gestão",
          icon: "mdi-calendar-clock",
          has_permission: sessionStore.hasPermission([
            "absence.request.default-days.manage.department",
            "absence.request.default-days.manage.any",
          ]),
          exact: true,
          link: "/absences/manage",
          items: [],
        }
      ],
    },
    {
      title: "Avaliações",
      icon: "mdi-file-chart",
      has_permission: sessionStore.hasPermission([
        "self.assessment.create.self", "self.assessment.create.any", "self.assessment.create.department",
        "self.assessment.list.self", "self.assessment.list.any", "self.assessment.list.department",
        "self.assessment.get.self", "self.assessment.get.any", "self.assessment.get.department",
        "self.assessment.update.self", "self.assessment.update.any", "self.assessment.update.department",
        "self.assessment.submit.self", "self.assessment.submit.any", "self.assessment.submit.department",
        "ascending.assessment.list.any", "ascending.assessment.get.any", "ascending.assessment.update.any",
        "ascending.assessment.create.self", "ascending.assessment.create.department", "ascending.assessment.create.any",
        "downward.assessment.manage", "downward.assessment.create.any", "downward.assessment.create.department",
        "assessment.model.list", "assessment.model.get", "assessment.model.create",
        "assessment.model.update", "assessment.model.delete",
      ]),
      exact: true,
      link: null,
      items: [
        {
          title: "Gerir Avaliações",
          icon: "mdi-star-cog",
          has_permission: sessionStore.hasPermission([
            "assessment.activation.manage",
          ]),
          exact: true,
          link: "/assessments/manage",
        },
        {
          title: "Auto-avaliações",
          icon: "mdi-head-snowflake",
          has_permission: sessionStore.hasPermission([
            "self.assessment.create.any", 
            "self.assessment.create.department", 
            "self.assessment.create.self", 
            "self.assessment.get.any", 
            "self.assessment.get.department", 
            "self.assessment.get.self", 
            "self.assessment.list.any", 
            "self.assessment.list.department", 
            "self.assessment.list.self", 
            "self.assessment.submit.any", 
            "self.assessment.submit.department",
            "self.assessment.submit.self", 
            "self.assessment.update.any", 
            "self.assessment.update.department", 
            "self.assessment.update.self", 
          ]),
          exact: true,
          link: "/assessments/self"
        },
        {
          title: "Avaliações Ascendentes",
          icon: "mdi-account-arrow-up",
          has_permission: sessionStore.hasPermission([
            "ascending.assessment.create.any",
            "ascending.assessment.create.department", 
            "ascending.assessment.create.self", 
            "ascending.assessment.get.any", 
            "ascending.assessment.list.any", 
            "ascending.assessment.update.any", 
          ]),
          exact: true,
          link: "/assessments/ascending"
        },
        {
          title: "Avaliações",
          icon: "mdi-account-supervisor-circle",
          has_permission: sessionStore.hasPermission([
            "downward.assessment.manage", 
            "downward.assessment.create.any", 
            "downward.assessment.create.department"
          ]),
          exact: true,
          link: "/assessments/downward"
        },
        {
          title: "Modelos",
          icon: "mdi-table-multiple",
          has_permission: sessionStore.hasPermission([
            "assessment.model.list", 
            "assessment.model.get", 
            "assessment.model.create", 
            "assessment.model.update", 
            "assessment.model.delete"
          ]),
          exact: true,
          link: "/assessments/models",
        },
        {
          title: "Comp. Técnicas",
          icon: "mdi-wrench-check",
          has_permission: sessionStore.hasPermission([
            "assessment.model.list", 
            "assessment.model.get", 
            "assessment.model.create", 
            "assessment.model.update", 
            "assessment.model.delete"
          ]),
          exact: true,
          link: "/assessments/competencies/technical",
        },
        {
          title: "Comp. Pessoais",
          icon: "mdi-account-multiple-check",
          has_permission: sessionStore.hasPermission(["assessment.model.list", "assessment.model.get", "assessment.model.create", "assessment.model.update", "assessment.model.delete"]),
          exact: true,
          link: "/assessments/competencies/personal",
        },
        {
          title: "Objectivos",
          icon: "mdi-list-status",
          has_permission: false,
          exact: true,
          link: "/assessments/objectives",
        },
      ],
    },
    {
      title: "Formulários",
      icon: "mdi-progress-question",
      has_permission: sessionStore.hasPermission([
        "forms.list",
        "forms.submissions.list.self",
        "forms.submissions.list.any",
      ]),
      exact: true,
      link: "",
      items: [
        {
          title: "Gerir",
          icon: "mdi-file-cog",
          has_permission: sessionStore.hasPermission([
            "forms.list",
            "forms.get",
            "forms.submissions.list.self",
            "forms.submissions.list.any",
            "forms.submissions.create",
            "forms.submissions.get.self",
            "forms.submissions.get.any"
          ]),
          exact: true,
          link: "/forms/manage",
          items: [],
        }
      ]
    },
    {
      title: "Exportações",
      icon: "mdi-download",
      has_permission: sessionStore.hasPermission([
        "user.department.membership.request.export.any",
        "user.department.membership.request.export.department",
        "user.department.membership.request.view.operators.any",
        "absence.request.export",
      ]),
      exact: true,
      link: "/exports",
      items: [],
    },
  ]

  items.value = menu.filter((item) => {
    return item.has_permission
  })
}

onMounted(() => {
  // NEXUS-148 - Alice Serina - 21/10/2026
  // the user and Session are already obtained in main.js before any navigation
  // and cannot be reassinged here

  theme.global.name.value = +localStorage["dark-mode"]
        ? "dark"
        : "light";

  let currentTheme = sessionStore.user?.theme ?? [];

  currentTheme = currentTheme.length === 0
      ? themes[0]
      : currentTheme;

  setTheme(currentTheme);
  setMenu();

  loading.value = false;

  setCssVars();


  showMini.value = localStorage['menu-mini'] === 'true'

  const showMenuStorage = localStorage['menu-show']

  showMenu.value = showMenuStorage !== undefined
    ? showMenuStorage === 'true'
    : true

  showMenu.value = showMenu.value || window.innerWidth >= 960

  rotateAvatar.value = false
  canRotate.value = true
})

function debugConfirm(confirm) {
  alert('Clique no confirmar do Layout UniversalDialog')
  console.log('Clique no confirmar do Layout UniversalDialog')
  confirm()
}
</script>

